import React from 'react';
import { Paper, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { useModePanelStyle, CompPanelHeader, BaseCompProps } from '@moderepo/mode-app-components';
import { AggregatedMetricData, LabelPosition, WidgetDataViewLayout, GaugeChart, GaugeColorType, MetricsMetadata, ValueThresholdReachedInfo,
    ValueThresholdMonitorSeverity } from '../../containers';
import { NO_VALUE } from '../../utils/constants';



const useStyle = makeStyles((theme: Theme) => {


    return {
        root: {
            overflow : 'hidden',
            height   : 500,
            minHeight: 'unset',
        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        gaugesContainer: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            width         : '100%',
            height        : '100%',
            overflow      : 'auto',
            flexDirection : 'column',

            [`&.layout-${WidgetDataViewLayout.VERTICAL}`]: {
                flexDirection: 'column',
            },
            [`&.layout-${WidgetDataViewLayout.HORIZONTAL}`]: {
                flexDirection: 'row',
            },
        },

        gaugeContainer: {
            flex          : 1,
            width         : '100%',
            padding       : theme.spacing(1),
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',

            '& .gauge': {
                width: '100%',
            },

            '& .date': {
                fontSize  : 15,
                color     : theme.palette.text.primary,
                textAlign : 'center',
                fontWeight: 'normal',
                width     : '100%',
            },
        },
    };
}, {
    name: 'ValueDashboardWidgetGaugeDataViewComp', index: 1,
});



export interface ValueDashboardWidgetGaugeDataViewCompProps extends BaseCompProps {
    readonly layout: WidgetDataViewLayout;
    readonly showLabel?: LabelPosition | undefined;
    readonly valueDisplayColor?: string | undefined;

    // Only needed to create unique id for the gauge chart
    readonly widgetId: number;

    // The data for the metrics
    readonly aggregatedData: AggregatedMetricData;

    // Localized metrics' data used for display
    readonly localizedAggregatedData?: {
        readonly [metricName: string]: string;
    } | undefined;

    // Whether or not the widget has threshold monitor turned on
    readonly monitorValueThresholds?: boolean | undefined;

    /**
     * The status of threshold monitor ONLY if this widget has monitor threshold enabled. If not, this should be set to undefined. If this is NOT
     * undefined, we will assume this widget has threshold monitor enabled. Since there can be more than 1 metrics displayed
     * and not all metrics reached threshold, this will be a map of metricName => threshold reaching info for only the metric that
     * reached the threshold. e.g.
     *      {
     *          temperature: {
     *              type: 'too_high', severity: 'critical'      // this mean the 'temperature' reached the threshold
     *          },
     *          pressure: undefined,                            // this mean the 'pressure' value is fine
     *      }
     */
    readonly thresholdMonitorStatus?: {
        readonly [metricValueName: string]: ValueThresholdReachedInfo | undefined;
    } | undefined;

    // The threshold settings metadata
    readonly thresholdMonitorSettings?: MetricsMetadata | undefined;

    // The range settings metadata
    readonly yAxisRangeSettings?: MetricsMetadata | undefined;

    readonly noDataMessageComp: React.ReactNode;
}



/**
 * This is the UI/Data View for the ValueDashboardWidget. This component will use GAUGE chart to display the time series values as 1 SINGLE VALUE.
 */
export const ValueDashboardWidgetGaugeDataViewComp: React.FC<ValueDashboardWidgetGaugeDataViewCompProps> = (
    props: ValueDashboardWidgetGaugeDataViewCompProps,
) => {
    const panelClasses = useModePanelStyle();
    const classes = useStyle();
    const theme = useTheme();
    const { aggregatedData } = props;


    return (
        <Paper
            elevation={2}
            className={clsx(
                panelClasses.root, classes.root, props.className, props.showCustomActionOnHover && 'show-custom-action-on-hover',
            )}
        >
            <CompPanelHeader {...props} />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {aggregatedData && aggregatedData.numericMetrics && aggregatedData.numericMetrics.length > 0 && (
                    <>
                        <div className={clsx(classes.gaugesContainer, `layout-${props.layout}`)}>
                            {aggregatedData.numericMetrics.map((metric) => {

                                const chartId = `ValueDashboardWidgetGaugeDataViewComp-gauge-${props.widgetId}-${metric.name}`;
                                const metricDataPoint = aggregatedData.numericMetricData?.[metric.name];
                                const thresholdAlertInfo = props.thresholdMonitorStatus?.[metric.name];
                                const thresholdSettings = props.thresholdMonitorSettings?.[metric.name];
                                const yAxisRange = props.yAxisRangeSettings?.[metric.name];

                                const numericValue = metricDataPoint?.value ?? undefined;
                                const stringValue = props.localizedAggregatedData?.[metric.name] ?? numericValue?.toString() ?? NO_VALUE;

                                const valueColor = (() => {
                                    // If monitor threshold is turned on, we need to use the color based on the threshold alert
                                    if (props.monitorValueThresholds) {
                                        if (thresholdAlertInfo?.severity === ValueThresholdMonitorSeverity.CRITICAL) {
                                            return theme.palette.error.main;
                                        }
                                        if (thresholdAlertInfo?.severity === ValueThresholdMonitorSeverity.WARNING) {
                                            return theme.palette.warning.main;
                                        }
                                        return theme.palette.success.main;
                                    }
                                    return props.valueDisplayColor;
                                })();

                                return (
                                    <div
                                        key={metric.name}
                                        className={clsx(classes.gaugeContainer)}
                                    >
                                        <GaugeChart
                                            className="gauge"
                                            chartId={chartId}
                                            metric={metric}
                                            value={numericValue}
                                            displayValue={stringValue}
                                            showMetricName={props.showLabel !== 'none' ? props.showLabel : undefined}
                                            colorType={props.monitorValueThresholds && thresholdSettings
                                                ? GaugeColorType.THRESHOLDS : GaugeColorType.SOLID}
                                            thresholds={props.monitorValueThresholds && thresholdSettings ? {
                                                criticallyLow : thresholdSettings.lowValueCriticalThreshold,
                                                low           : thresholdSettings.lowValueWarningThreshold,
                                                high          : thresholdSettings.highValueWarningThreshold,
                                                criticallyHigh: thresholdSettings.highValueCriticalThreshold,
                                            } : undefined}
                                            range={yAxisRange?.yAxisMin !== undefined && yAxisRange.yAxisMax !== undefined ? {
                                                min: yAxisRange.yAxisMin,
                                                max: yAxisRange.yAxisMax,
                                            } : undefined}
                                            style={{
                                                valueColor,
                                            }}
                                        />
                                        {metricDataPoint?.dateString && (
                                            <div className="date">
                                                {metricDataPoint.dateString}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/*
                    If there are no metric or no data then show empty data message
                */}
                {(!props.aggregatedData || !props.aggregatedData.numericMetrics || props.aggregatedData.numericMetrics.length === 0)
                     && props.noDataMessageComp}
            </div>
        </Paper>
    );
};
